import { useEffect, useState } from 'react'

import { FvButton } from '@fv/client-components'
import type { FullShipment } from '@fv/client-types'

import { HideForVendor, ShipperOnly } from '@/features/auth/HasRole'
import { useHasPermission, useRole } from '@/features/auth/hooks'
import { AddRateButton } from '@/features/quote/ManualRatePanel'
import { CancelShipmentPrompt } from '@/features/shipment-details-panel/CancelShipmentPrompt'
import { UnCancelShipmentPrompt } from '@/features/shipment-details-panel/UnCancelShipmentPrompt'
import { useCancelLoad, useEditBookingPage } from '@/hooks/shipments'
import { useRepeatShipment } from '@/hooks/shipments/useRepeatShipment'

import { ActionList, ActionListItem } from '../../ActionList'
import { ShipperTooltip } from '../../ShipperTooltip'

type ActivePanelFooterProps = {
  isShowingRates: boolean
  load: FullShipment
  setShowingRates: (s: boolean) => void
}

export const ActivePanelFooter = ({
  isShowingRates,
  load,
  setShowingRates,
}: ActivePanelFooterProps) => {
  const [showCancelDialog, setShowCancelDialog] = useState(false)
  const [showUncancelDialog, setShowUncancelDialog] = useState(false)
  const hasCancelPermission = useHasPermission('load-canceler')
  const role = useRole()

  const { canEdit, composeEditLink } = useEditBookingPage(load)
  const cancelLoad = useCancelLoad(load.loadId)
  const isCanceling = cancelLoad.isPending
  const { loadId, status } = load
  const isCanceled = status === 'canceled'
  const hasCancelButton = !isCanceled && hasCancelPermission
  const hasUncancelButton = isCanceled && hasCancelPermission
  const repeat = useRepeatShipment(load)

  // biome-ignore lint/correctness/useExhaustiveDependencies: bypass
  useEffect(() => {
    setShowCancelDialog(false)
  }, [loadId])

  const handleCancelConfirm = () => {
    if (isCanceling) return

    cancelLoad.mutate(false, {
      onSuccess: () => {
        setShowCancelDialog(false)
        setShowingRates(false)
      },
    })
  }

  if (
    role === 'vendor' ||
    (!canEdit && !hasCancelButton && !isShowingRates && !hasUncancelButton)
  )
    return null

  return (
    <>
      <ActionList className="fa-width-auto">
        {isShowingRates && (
          <AddRateButton icon="plus" load={load}>
            Rate
          </AddRateButton>
        )}

        {canEdit && (
          <ActionListItem className="" icon="pen-alt" to={composeEditLink()}>
            Edit load
          </ActionListItem>
        )}
        <ShipperOnly permissions={['load-creator']}>
          <ActionListItem
            icon="clone"
            onClick={repeat}
            tooltip="Repeat this shipment"
          >
            Repeat
          </ActionListItem>
        </ShipperOnly>

        {hasCancelButton && (
          <HideForVendor>
            <ActionListItem
              tooltip="Cancel shipment"
              icon="times"
              loading={isCanceling}
              onClick={() => setShowCancelDialog(true)}
            />
          </HideForVendor>
        )}

        {hasUncancelButton && (
          <HideForVendor>
            <ShipperTooltip label="Uncancel shipment">
              <FvButton icon="redo" onClick={() => setShowUncancelDialog(true)}>
                Uncancel
              </FvButton>
            </ShipperTooltip>
          </HideForVendor>
        )}
      </ActionList>

      {showCancelDialog && (
        <CancelShipmentPrompt
          className="mt-4"
          loading={isCanceling}
          onConfirm={handleCancelConfirm}
          onDecline={() => setShowCancelDialog(false)}
        />
      )}

      {showUncancelDialog && (
        <UnCancelShipmentPrompt
          className="mt-4"
          onClose={() => setShowUncancelDialog(false)}
          loadId={loadId}
        />
      )}
    </>
  )
}
